import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowBack, Close, ContentPaste, Download, UploadFile } from '@mui/icons-material';
import toast from 'react-hot-toast';
import { familyStaffApi } from '../Family/parentApi';
import {
  teacherModuleAPI,
  downloadSessionHomeworkPdf,
  resolvePublicAssetUrl,
} from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { isClassLogComplete } from '../../utils/classSessionLog';
import { isAllStudentsAbsentFromStudents, isSessionWorkWaived } from '../../utils/sessionWorkRequirements';
import { getTodayYMDInTimezone, isSessionDateInFuture } from '../../utils/dateTimeUtils';
import AuthImage from '../../components/AuthImage';

function useQueryParam(name) {
  const location = useLocation();
  return React.useMemo(() => new URLSearchParams(location.search || '').get(name), [location.search, name]);
}

function isEmptyText(v) {
  return !String(v || '').trim();
}

function isIanaTimezone(tz) {
  return typeof tz === 'string' && tz.includes('/');
}

function getIanaOffsetMinutes(timeZone, utcDate) {
  const tz = (timeZone && String(timeZone).trim()) ? String(timeZone).trim() : 'UTC';
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = dtf.formatToParts(utcDate);
  const get = (type) => parts.find((p) => p.type === type)?.value;
  const y = Number(get('year'));
  const m = Number(get('month'));
  const d = Number(get('day'));
  const hh = Number(get('hour'));
  const mm = Number(get('minute'));
  const ss = Number(get('second'));
  const asUTC = Date.UTC(y, m - 1, d, hh, mm, ss);
  return (asUTC - utcDate.getTime()) / 60000;
}

function zonedDateTimeToUtcMs(dateStrYYYYMMDD, timeStrHHMMSS, timeZone) {
  if (!dateStrYYYYMMDD || !timeStrHHMMSS) return null;
  const tz = (timeZone && String(timeZone).trim()) ? String(timeZone).trim() : 'UTC';
  const [y, m, d] = String(dateStrYYYYMMDD).slice(0, 10).split('-').map(Number);
  const t = String(timeStrHHMMSS).trim().slice(0, 8);
  const normalized = t.length === 5 ? `${t}:00` : t;
  const [hh, mm, ss] = normalized.split(':').map(Number);
  if (!y || !m || !d || Number.isNaN(hh) || Number.isNaN(mm)) return null;

  const guessUtcMs = Date.UTC(y, m - 1, d, hh, mm, ss || 0);
  const guessDate = new Date(guessUtcMs);

  const offsetsHours = {
    UTC: 0,
    IST: 5.5,
    EST: -5,
    PST: -8,
    CST: -6,
    MST: -7,
    GMT: 0,
    AEST: 10,
    AEST_QLD: 10,
    AWST: 8,
    ACST: 9.5,
    AST: -4,
    NST: -3.5,
    HST: -10,
    AKST: -9,
  };

  const offsetMinutes = isIanaTimezone(tz)
    ? getIanaOffsetMinutes(tz, guessDate)
    : (offsetsHours[tz] != null ? offsetsHours[tz] * 60 : 0);

  return guessUtcMs - offsetMinutes * 60000;
}

export default function TeacherHomeworkSession() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const sessionId = useQueryParam('sessionId');
  const [dueDate, setDueDate] = React.useState('');
  const { user, isTeacher, isTeachingLead, isTeacherSupervisor, isCountryManager, isAdmin } = useAuth();
  const sessionEnabled = !!sessionId;

  const detailQuery = useQuery(
    ['teacherSessionDetail', sessionId],
    async () => {
      const res = await teacherModuleAPI.getSessionDetail(sessionId);
      return res.data?.data;
    },
    { enabled: sessionEnabled }
  );

  const approvalQuery = useQuery(
    ['teacherSessionApproval', sessionId],
    async () => {
      const res = await teacherModuleAPI.getSessionApproval(sessionId);
      return res.data?.data;
    },
    { enabled: sessionEnabled && (isTeacher() || isTeachingLead() || isAdmin()) }
  );

  const templatesQuery = useQuery(['teacherHomeworkTemplates'], async () => {
    const res = await teacherModuleAPI.getHomeworkTemplates();
    return res.data?.data || [];
  });

  const homeworkPdfsQuery = useQuery(
    ['teacherSessionHomeworkPdfs', sessionId],
    async () => {
      const res = await teacherModuleAPI.listSessionHomeworkPdfs(sessionId);
      return res.data?.data || [];
    },
    { enabled: sessionEnabled, refetchOnWindowFocus: true }
  );

  const [selectedTemplateId, setSelectedTemplateId] = React.useState('');
  const [uploadedFile, setUploadedFile] = React.useState(null);
  const [editorText, setEditorText] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const textHydrated = React.useRef(false);
  React.useEffect(() => {
    textHydrated.current = false;
  }, [sessionId]);
  React.useEffect(() => {
    if (textHydrated.current || !detailQuery.data) return;
    textHydrated.current = true;
    setEditorText(String(detailQuery.data?.sessionLog?.homework_message || ''));
  }, [detailQuery.data]);
  const [openApprovalRequest, setOpenApprovalRequest] = React.useState(false);
  const [approvalRequestReason, setApprovalRequestReason] = React.useState('');

  const session = detailQuery.data?.session;
  const sessionLog = detailQuery.data?.sessionLog || null;
  const students = React.useMemo(
    () => detailQuery.data?.students || [],
    [detailQuery.data?.students]
  );

  const approvalStatus = approvalQuery.data?.status || 'none'; // none|pending|approved|rejected
  const permissions = detailQuery.data?.permissions;
  const allStudentsAbsent = React.useMemo(
    () => isAllStudentsAbsentFromStudents(students),
    [students]
  );
  const sessionWorkWaived = React.useMemo(
    () =>
      isSessionWorkWaived({
        allStudentsAbsent,
        isCancelled: !!permissions?.isCancelled || !!permissions?.isHoliday,
      }),
    [allStudentsAbsent, permissions?.isCancelled, permissions?.isHoliday]
  );

  const needsApproval = React.useMemo(() => {
    if (sessionWorkWaived) return false;
    if (permissions?.beforeBatchStart) return false;
    if (!session?.session_date) return false;
    const tz = session?.teacher_timezone || user?.timezone || 'UTC';
    const endUtcMs = zonedDateTimeToUtcMs(session.session_date, session?.end_time || '', tz);
    if (endUtcMs) {
      const cutoffMs = endUtcMs + 48 * 60 * 60 * 1000;
      if (Date.now() <= cutoffMs) return false;
    }

    const today = getTodayYMDInTimezone(tz);
    const isPastOrToday = session.session_date <= today;
    if (!isPastOrToday) return false;

    const attendanceMissing = students.length ? students.some((s) => !s.attendanceStatus) : false;
    const notesMissing = !isClassLogComplete(
      sessionLog?.classLogJson || sessionLog?.class_log_json,
      sessionLog?.batch_notes
    );
    const hwMissing =
      isEmptyText(sessionLog?.homework_message) &&
      !sessionLog?.homework_template_id &&
      isEmptyText(sessionLog?.homework_pdf_url) &&
      !(detailQuery.data?.homeworkImages || []).length;
    const logMissing = notesMissing || hwMissing || !sessionLog?.is_completed;
    return attendanceMissing || logMissing;
  }, [detailQuery.data?.homeworkImages, sessionWorkWaived, permissions?.beforeBatchStart, session?.end_time, session?.session_date, session?.teacher_timezone, sessionLog, students, user?.timezone]);

  const canWriteSession = permissions?.canWriteSession ?? false;

  const canEdit = React.useMemo(() => {
    if (!canWriteSession) return false;
    if (!isTeacher()) return true;
    if (!needsApproval) return true;
    if (isAdmin()) return true;
    return approvalStatus === 'approved';
  }, [approvalStatus, isAdmin, isTeacher, needsApproval, canWriteSession]);

  const supervisorViewOnly =
    (isTeacherSupervisor() || isCountryManager()) && !isAdmin() && !canWriteSession;

  const requestApproval = async () => {
    try {
      if (!sessionId) return;
      await teacherModuleAPI.requestSessionApproval(sessionId, {
        requestReason: approvalRequestReason.trim() || null,
      });
      toast.success('Approval requested');
      setOpenApprovalRequest(false);
      setApprovalRequestReason('');
      approvalQuery.refetch();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to request approval');
    }
  };

  const downloadTemplateUrl = (() => {
    const t = (templatesQuery.data || []).find((x) => String(x.id) === String(selectedTemplateId));
    return t?.fileUrl || '';
  })();

  if (!sessionId) {
    return <Alert severity="error">Missing sessionId.</Alert>;
  }

  if (detailQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!session) {
    return <Alert severity="error">Session not found.</Alert>;
  }

  const sessionTz = session?.teacher_timezone || user?.timezone;
  const isBeforeBatchStart = !!permissions?.beforeBatchStart;
  const isFutureSession =
    isBeforeBatchStart || isSessionDateInFuture(session.session_date, sessionTz);
  const canMutateHomework = canEdit && !isFutureSession;

  const handleUploadFilled = async () => {
    if (!canMutateHomework) {
      toast.error(isFutureSession ? 'Homework unlocks on the class date' : 'Approval required');
      return;
    }
    if (!uploadedFile) {
      toast.error('Choose a PDF or DOCX file first');
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', uploadedFile);
      if (dueDate) fd.append('dueDate', dueDate);
      const res = await teacherModuleAPI.uploadSessionHomework(sessionId, fd);
      const pdfUrl = res?.data?.data?.pdfUrl;
      toast.success('Homework saved');
      queryClient.invalidateQueries(['teacherSessionHomeworkPdfs', sessionId]);
      if (pdfUrl) {
        try {
          const dlName = `${session?.batch_name || 'Class'}-homework.pdf`.replace(/[^\w.\- ]+/g, '_');
          await downloadSessionHomeworkPdf(sessionId, pdfUrl, dlName);
        } catch (err) {
          toast.error(err.message || 'Could not download PDF');
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const handleGeneratePdf = async () => {
    if (!canMutateHomework) {
      toast.error(isFutureSession ? 'Homework unlocks on the class date' : 'Approval required');
      return;
    }
    if (!editorText.trim()) {
      toast.error('Enter homework text first');
      return;
    }
    setBusy(true);
    try {
      const res = await teacherModuleAPI.generateSessionHomeworkPdf(sessionId, {
        dueDate: dueDate || null,
        title: `${session.batch_name || 'Class'} Homework`,
        text: editorText,
      });
      const pdfUrl = res?.data?.data?.pdfUrl;
      toast.success('PDF generated — downloading');
      queryClient.invalidateQueries(['teacherSessionHomeworkPdfs', sessionId]);
      if (pdfUrl) {
        try {
          const dlName = `${session?.batch_name || 'Class'}-homework.pdf`.replace(/[^\w.\- ]+/g, '_');
          await downloadSessionHomeworkPdf(sessionId, pdfUrl, dlName);
        } catch (err) {
          toast.error(err.message || 'Could not download PDF');
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to generate PDF');
    } finally {
      setBusy(false);
    }
  };

  const homeworkSent = !!(sessionLog?.homework_sent_at || sessionLog?.homeworkSentAt);

  const handleMarkHomeworkSent = async () => {
    if (!canMutateHomework) {
      toast.error(isFutureSession ? 'Homework unlocks on the class date' : 'Approval required');
      return;
    }
    setBusy(true);
    try {
      const textToSave = editorText.slice(0, 5000);
      await teacherModuleAPI.markSessionHomeworkSent(sessionId, {
        dueDate: dueDate || null,
        text: textToSave,
      });
      if (editorText.length > 5000) {
        toast('Saved the first 5000 characters.');
      }
      await queryClient.invalidateQueries(['teacherSessionDetail', sessionId]);
      if (session?.batch_id) queryClient.invalidateQueries(['batchPendingItems', session.batch_id]);
      queryClient.invalidateQueries(['teacherMyPendingItems']);
      toast.success('Homework saved and marked as sent');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to mark homework as sent');
    } finally {
      setBusy(false);
    }
  };

  const homeworkImages = detailQuery.data?.homeworkImages || [];

  const uploadHomeworkImageFile = async (file) => {
    const fd = new FormData();
    fd.append('file', file, file.name || 'screenshot.png');
    await teacherModuleAPI.uploadSessionHomeworkImage(sessionId, fd);
  };

  const savePastedImages = async (files) => {
    if (!canMutateHomework) {
      toast.error(isFutureSession ? 'Homework unlocks on the class date' : 'Approval required');
      return;
    }
    const list = (files || []).filter(Boolean);
    if (!list.length) {
      toast.error('Copy a screenshot first, then paste it here');
      return;
    }
    setBusy(true);
    try {
      for (const file of list) {
        await uploadHomeworkImageFile(file);
      }
      await queryClient.invalidateQueries(['teacherSessionDetail', sessionId]);
      toast.success(list.length > 1 ? 'Screenshots saved' : 'Screenshot saved');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not save screenshot');
    } finally {
      setBusy(false);
    }
  };

  const handleInstructionPaste = (event) => {
    const items = Array.from(event.clipboardData?.items || []);
    const files = items
      .filter((item) => String(item.type || '').startsWith('image/'))
      .map((item) => item.getAsFile())
      .filter(Boolean);
    if (!files.length) return;
    event.preventDefault();
    savePastedImages(files);
  };

  const handlePasteFromClipboard = async () => {
    if (!navigator.clipboard?.read) {
      toast.error('Click the box and press Ctrl+V to paste a screenshot');
      return;
    }
    try {
      const items = await navigator.clipboard.read();
      const files = [];
      for (const item of items) {
        const type = item.types.find((t) => t.startsWith('image/'));
        if (!type) continue;
        const blob = await item.getType(type);
        const ext = type.includes('png') ? 'png' : type.includes('webp') ? 'webp' : 'jpg';
        files.push(new File([blob], `screenshot.${ext}`, { type }));
      }
      await savePastedImages(files);
    } catch (e) {
      if (e?.response) {
        toast.error(e.response?.data?.message || 'Could not save screenshot');
        return;
      }
      toast.error('Could not read the clipboard. Click the box and press Ctrl+V.');
    }
  };

  const handleRemoveImage = async (imageId) => {
    setBusy(true);
    try {
      await teacherModuleAPI.deleteSessionHomeworkImage(sessionId, imageId);
      await queryClient.invalidateQueries(['teacherSessionDetail', sessionId]);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not remove screenshot');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mb: 1 }}>
        Back
      </Button>

      <Typography variant="h4" fontWeight={900} gutterBottom>
        Homework
      </Typography>
      {supervisorViewOnly ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Read-only view. You can download homework PDFs; upload is for the assigned teacher.
        </Alert>
      ) : null}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {session.batch_name} • {session.session_date} {String(session.start_time || '').slice(0, 5)}–
        {String(session.end_time || '').slice(0, 5)}
      </Typography>

      {isFutureSession ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          This class is in the future. Homework upload and PDF generation unlock on the class date.
        </Alert>
      ) : null}

      {permissions?.isHoliday ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          This class is a holiday. Homework is not required.
        </Alert>
      ) : sessionWorkWaived && !permissions?.isCancelled ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          All students were marked absent. Homework is not required for this class.
        </Alert>
      ) : null}

      {needsApproval && !canEdit && !isFutureSession ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Approval required to fill attendance, homework, or batch notes after 48 hours from the scheduled end time of this session.
          <Box sx={{ mt: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setOpenApprovalRequest(true)}
              disabled={approvalStatus === 'pending' || approvalStatus === 'approved'}
            >
              {approvalStatus === 'pending' ? 'Approval requested' : approvalStatus === 'approved' ? 'Approved' : 'Request approval'}
            </Button>
          </Box>
        </Alert>
      ) : null}

      <Dialog
        open={openApprovalRequest}
        onClose={() => {
          setOpenApprovalRequest(false);
          setApprovalRequestReason('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Request exceptional approval</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Optionally explain why you need approval to complete this class after the normal window.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Reason (optional)"
            value={approvalRequestReason}
            onChange={(e) => setApprovalRequestReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenApprovalRequest(false);
              setApprovalRequestReason('');
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={requestApproval}>
            Submit request
          </Button>
        </DialogActions>
      </Dialog>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={900} sx={{ mb: 1 }}>
          Generated homework PDFs
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Download any PDF created for this class (from &quot;Generate PDF&quot; or upload). Older generated files are kept until removed from the server.
        </Typography>
        {homeworkPdfsQuery.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={22} />
          </Box>
        ) : homeworkPdfsQuery.isError ? (
          <Alert severity="warning">Could not load PDF list.</Alert>
        ) : (homeworkPdfsQuery.data || []).length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No PDFs yet. Generate one from the text below or upload a filled file.
          </Typography>
        ) : (
          <List dense disablePadding sx={{ bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 2 }}>
            {(homeworkPdfsQuery.data || []).map((row) => {
              const name = row.filename || String(row.pdfUrl || '').split('/').filter(Boolean).pop() || 'homework.pdf';
              const when = row.generatedAt
                ? new Date(row.generatedAt).toLocaleString()
                : row.source === 'saved'
                  ? 'Latest saved (open to view)'
                  : '—';
              const size =
                row.sizeBytes != null ? `${Math.max(1, Math.round(row.sizeBytes / 1024))} KB` : null;
              return (
                <ListItem key={row.pdfUrl} divider sx={{ py: 1 }}>
                  <ListItemText
                    primary={name}
                    secondary={[when, size].filter(Boolean).join(' · ')}
                    primaryTypographyProps={{ fontWeight: 800 }}
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title="Download PDF">
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => {
                          downloadSessionHomeworkPdf(sessionId, row.pdfUrl, name).catch((err) =>
                            toast.error(err.message || 'Could not download PDF')
                          );
                        }}
                      >
                        <Download fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              );
            })}
          </List>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={900} sx={{ mb: 1 }}>
          Expected completion date
        </Typography>
        <TextField
          type="date"
          size="small"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          disabled={!canMutateHomework}
          sx={{ width: 240 }}
        />
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle1" fontWeight={900}>
            Choose Homework Template
          </Typography>
          <Button
            size="small"
            onClick={() => {
              const t = downloadTemplateUrl;
              if (!t) toast.error('Select a template first');
              else window.open(resolvePublicAssetUrl(t), '_blank', 'noopener,noreferrer');
            }}
            disabled={!downloadTemplateUrl}
            sx={{ textTransform: 'none', fontWeight: 800 }}
          >
            Download selected template
          </Button>
        </Stack>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Card variant="outlined" sx={{ borderRadius: 3, borderStyle: 'dashed' }}>
              <CardActionArea component="label">
                <CardContent sx={{ minHeight: 140, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <UploadFile sx={{ mb: 1 }} />
                  <Typography fontWeight={800}>Upload Filled</Typography>
                  <Typography variant="caption" color="text.secondary">
                    PDF or DOCX
                  </Typography>
                  <input
                    hidden
                    type="file"
                    accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    disabled={!canMutateHomework}
                    onChange={(e) => setUploadedFile(e.target.files?.[0] || null)}
                  />
                </CardContent>
              </CardActionArea>
            </Card>
            {uploadedFile ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Selected: {uploadedFile.name}
              </Typography>
            ) : null}
            <Button
              variant="contained"
              onClick={handleUploadFilled}
              disabled={!canMutateHomework || busy || !uploadedFile}
              sx={{ mt: 1, borderRadius: 2, textTransform: 'none', fontWeight: 900 }}
            >
              Upload & Create PDF
            </Button>
          </Grid>

          {(templatesQuery.data || []).slice(0, 6).map((t) => (
            <Grid item xs={12} sm={6} md={3} key={t.id}>
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 3,
                  borderColor: String(t.id) === String(selectedTemplateId) ? '#1976d2' : 'rgba(0,0,0,0.12)',
                }}
              >
                <CardActionArea onClick={() => setSelectedTemplateId(t.id)}>
                  <CardContent sx={{ minHeight: 140 }}>
                    <Typography fontWeight={900} sx={{ mb: 0.5 }}>
                      {t.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Template file
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mb: 2 }} onPaste={handleInstructionPaste}>
        <Typography variant="subtitle1" fontWeight={900} sx={{ mb: 1 }}>
          Teacher’s Instructions (Option B)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Type the assignment, or paste a screenshot with Ctrl+V. Homework sent saves the text without creating a PDF. A screenshot is saved as soon as you paste it.
        </Typography>
        <Box
          tabIndex={0}
          sx={{
            mb: 1.5,
            p: 1.5,
            borderRadius: 2,
            border: '1px dashed',
            borderColor: 'divider',
            bgcolor: 'rgba(25,118,210,0.04)',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
            <ContentPaste fontSize="small" />
            <Typography variant="body2" sx={{ flex: 1, minWidth: 180 }}>
              Paste a screenshot here
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={handlePasteFromClipboard}
              disabled={!canMutateHomework || busy}
              sx={{ textTransform: 'none', fontWeight: 800 }}
            >
              Paste screenshot
            </Button>
          </Stack>
          {homeworkImages.length > 0 ? (
            <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap' }}>
              {homeworkImages.map((img) => (
                <Box key={img.id} sx={{ position: 'relative', width: 120, height: 120 }}>
                  <AuthImage
                    src={img.url}
                    alt={img.originalName || 'Homework screenshot'}
                    style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 8 }}
                  />
                  {canMutateHomework ? (
                    <IconButton
                      size="small"
                      aria-label="Remove screenshot"
                      onClick={() => handleRemoveImage(img.id)}
                      disabled={busy}
                      sx={{
                        position: 'absolute',
                        top: 2,
                        right: 2,
                        bgcolor: 'rgba(255,255,255,0.9)',
                      }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  ) : null}
                </Box>
              ))}
            </Stack>
          ) : null}
        </Box>
        <TextField
          multiline
          minRows={6}
          fullWidth
          value={editorText}
          onChange={(e) => setEditorText(e.target.value)}
          disabled={!canMutateHomework || homeworkSent}
          placeholder="Type the assignment here..."
        />
        <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            onClick={handleGeneratePdf}
            disabled={!canMutateHomework || busy}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 900 }}
          >
            Generate PDF
          </Button>
          <Button
            variant={homeworkSent ? 'contained' : 'outlined'}
            color={homeworkSent ? 'success' : 'primary'}
            onClick={handleMarkHomeworkSent}
            disabled={!canMutateHomework || busy || homeworkSent}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 900 }}
          >
            Homework sent
          </Button>
        </Stack>
        {homeworkSent ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Homework is marked complete for this class.
          </Typography>
        ) : null}
      </Paper>

      {(detailQuery.data?.familyHomework || []).length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={900} sx={{ mb: 1 }}>
            Family submissions
          </Typography>
          {detailQuery.data.familyHomework.map((hw) => (
            <Box key={hw.submissionId} sx={{ mb: 1.5 }}>
              <Typography fontWeight={700}>{hw.studentName} · {hw.status}</Typography>
              {hw.body ? <Typography variant="body2">{hw.body}</Typography> : null}
              {(hw.files || []).map((f) => (
                <Button key={f.url} size="small" href={resolvePublicAssetUrl(f.url)} target="_blank" rel="noreferrer">
                  {f.name || 'File'}
                </Button>
              ))}
              {hw.status !== 'reviewed' ? (
                <Button
                  size="small"
                  variant="contained"
                  sx={{ ml: 1 }}
                  onClick={async () => {
                    try {
                      await familyStaffApi.reviewHomework(hw.submissionId);
                      toast.success('Marked reviewed');
                      detailQuery.refetch();
                    } catch (e) {
                      toast.error(e.response?.data?.message || 'Could not review');
                    }
                  }}
                >
                  Mark reviewed
                </Button>
              ) : null}
            </Box>
          ))}
        </Paper>
      )}
    </Box>
  );
}

